import { useEffect, useState, useMemo } from "react";
import api from "../../api";
import { Table, Form, InputGroup, Pagination } from "react-bootstrap";
import { Search } from "lucide-react";
import { ToastContainer } from 'react-toastify';
import "../css/others/cusSub.css";

export const CusSub = () => {
  const [subs, setSubs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchSubs = async () => {
    try {
      const response = await api.get('/view_sub');
      setSubs(response.data.sub || []);
    } catch (error) { 
      console.error("Error fetching data:", error); 
    }
  };

  useEffect(() => { fetchSubs(); }, []);

  // Filter logic
  const filteredSubs = useMemo(() => {
    return subs.filter(s => 
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      s.phone?.includes(searchTerm)
    );
  }, [subs, searchTerm]);

  // Pagination math
  const totalPages = Math.ceil(filteredSubs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="document-container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="page-title">Customer Submissions</h3>
        <InputGroup className="search-bar">
          <InputGroup.Text className="bg-white border-end-0">
            <Search size={18} className="text-muted" />
          </InputGroup.Text>
          <Form.Control 
            className="border-start-0"
            placeholder="Search name or mobile..." 
            onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
          />
        </InputGroup>
      </div>

      <div className="table-card shadow-sm">
        <Table hover striped responsive className="m-0">
          <thead>
            <tr>
              <th className="text-center" width="80">No.</th>
              <th>Customer Name</th>
              <th>Mobile Number</th>
              <th>District</th>
              <th>Service Content</th>
              <th>Service Other Detail</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((s, index) => (
                <tr key={s.id || index}>
                  <td className="text-center">{indexOfFirstItem + index + 1}</td>
                  <td className="fw-bold text-primary">{s.name}</td>
                  <td>{s.phone}</td>
                  <td>{s.district}</td>
                  <td className="text-muted italic small">{s.serviceTitleEn}</td>
                  <td className="text-muted italic small">{s.serviceOtherDetail || ""}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-5 text-muted">No submissions found</td>
              </tr>
            )}
          </tbody>
        </Table>
        
        {/* FOOTER: Matches your image */}
        {/* Standardized Pagination Footer */}
<div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
    
    {/* Left: Rows Per Page Selector */}
    <div className="d-flex align-items-center gap-2">
        <span className="text-muted small">Rows per page:</span>
        <Form.Select 
            size="sm" 
            style={{ width: '80px', borderRadius: '6px' }}
            value={itemsPerPage} 
            onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
            }}
        >
            {[10, 25, 50, 100].map(v => (
                <option key={v} value={v}>{v}</option>
            ))}
        </Form.Select>
    </div>

    {/* Middle: Entry Summary Text */}
    <div className="text-muted small fw-medium">
        {filteredSubs.length > 0 ? (
            <>
                Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredSubs.length)}</b> of <b>{filteredSubs.length}</b> submissions
            </>
        ) : (
            "No submissions available"
        )}
    </div>

    {/* Right: Modern Navigation Controls */}
    <Pagination className="mb-0 custom-pagination shadow-sm">
        <Pagination.First 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(1)} 
        />
        <Pagination.Prev 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
        />

        <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>

        <Pagination.Next 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
        />
        <Pagination.Last 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => handlePageChange(totalPages)} 
        />
    </Pagination>
</div>
      </div>
      <ToastContainer />
    </div>
  );
};